import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart2, FileText, ArrowLeft, Search,
  DollarSign, Receipt, CheckCircle2, AlertTriangle,
  TrendingUp, Percent, Download, Eye, ChevronDown, ChevronUp,
  ShoppingBag,
} from 'lucide-react';
import Layout from '../../../../components/Layout';
import {
  salesInvoiceService, SalesInvoiceListDTO, SalesInvoiceDTO,
  RestaurantOrderSummaryDTO, SalesSearchResultDTO,
} from '../../../../services/salesInvoiceService';
import { restaurantOrderService } from '../../../../services/restaurantOrderService';
import { RestaurantOrderDTO } from '../../../../types';
import './SalesSummaryReportPage.css';

// ── Local types ───────────────────────────────────────────────────────────────

interface SearchFilters {
  startDate: string;
  startTime: string;
  endDate:   string;
  endTime:   string;
}

interface SummaryMetrics {
  totalInvoices:           number;
  totalRestaurantOrders:   number;
  totalSales:              number;
  paidAmount:              number;
  unpaidAmount:            number;
  paidInvoices:            number;
  unpaidInvoices:          number;
  partiallyPaidInvoices:   number;
  averageTransactionValue: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const today    = () => new Date().toISOString().split('T')[0];
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split('T')[0];
};

const computeMetrics = (
  invoices: SalesInvoiceListDTO[],
  restaurantOrders: RestaurantOrderSummaryDTO[],
): SummaryMetrics => {
  const invoiceTotal      = invoices.reduce((s, i) => s + i.totalAmount, 0);
  const invoicePaid       = invoices.reduce((s, i) => s + i.amountPaid,  0);
  const invoiceBalance    = invoices.reduce((s, i) => s + i.balanceDue,  0);
  const restaurantTotal   = restaurantOrders.reduce((s, o) => s + o.totalAmount, 0);
  const restaurantPaid    = restaurantOrders.reduce((s, o) => s + o.amountPaid,  0);
  const restaurantBalance = restaurantOrders.reduce((s, o) => s + o.balanceDue,  0);
  const combinedTotal     = invoiceTotal + restaurantTotal;
  const combinedCount     = invoices.length + restaurantOrders.length;
  return {
    totalInvoices:           invoices.length,
    totalRestaurantOrders:   restaurantOrders.length,
    totalSales:              combinedTotal,
    paidAmount:              invoicePaid + restaurantPaid,
    unpaidAmount:            invoiceBalance + restaurantBalance,
    paidInvoices:            invoices.filter(i => i.paymentStatus === 'Paid').length,
    unpaidInvoices:          invoices.filter(i => i.paymentStatus === 'Unpaid').length,
    partiallyPaidInvoices:   invoices.filter(i => i.paymentStatus === 'PartiallyPaid').length,
    averageTransactionValue: combinedCount > 0 ? combinedTotal / combinedCount : 0,
  };
};

const statusBadgeClass = (s: string): string => {
  const map: Record<string, string> = {
    Draft:         'ims-badge ims-badge-draft',
    Issued:        'ims-badge ims-badge-confirmed',
    Paid:          'ims-badge ims-badge-approved',
    PartiallyPaid: 'ims-badge ims-badge-pending',
    Overdue:       'ims-badge ims-badge-rejected',
    Voided:        'ims-badge ims-badge-closed',
    Cancelled:     'ims-badge ims-badge-closed',
    Unpaid:        'ims-badge ims-badge-draft',
  };
  return map[s] ?? 'ims-badge ims-badge-draft';
};

const orderPaymentStatus = (o: RestaurantOrderSummaryDTO): string => {
  if (o.amountPaid <= 0) return 'Unpaid';
  return o.balanceDue > 0 ? 'PartiallyPaid' : 'Paid';
};

const restaurantStatusBadgeClass = (s: string): string => {
  const map: Record<string, string> = {
    Open:           'ims-badge ims-badge-draft',
    Sent:           'ims-badge ims-badge-confirmed',
    InPreparation:  'ims-badge ims-badge-pending',
    Ready:          'ims-badge ims-badge-pending',
    Completed:      'ims-badge ims-badge-approved',
    Voided:         'ims-badge ims-badge-closed',
    OnHold:         'ims-badge ims-badge-draft',
  };
  return map[s] ?? 'ims-badge ims-badge-draft';
};

// ── Component ─────────────────────────────────────────────────────────────────

const SalesSummaryReportPage: React.FC = () => {
  const { t }    = useTranslation();
  const navigate = useNavigate();
  const fmt = (n: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

  // ── Filter state
  const [filters, setFilters] = useState<SearchFilters>({
    startDate: monthAgo(),
    startTime: '00:00',
    endDate:   today(),
    endTime:   '23:59',
  });

  // ── Result state
  const [hasSearched,  setHasSearched]  = useState(false);
  const [searching,    setSearching]    = useState(false);
  const [invoices,     setInvoices]     = useState<SalesInvoiceListDTO[]>([]);
  const [restaurantOrders, setRestaurantOrders] = useState<RestaurantOrderSummaryDTO[]>([]);
  const [metrics,      setMetrics]      = useState<SummaryMetrics | null>(null);

  // ── Expand / detail state
  const [expandedRows,   setExpandedRows]   = useState<Set<number>>(new Set());
  const [invoiceDetails, setInvoiceDetails] = useState<Record<number, SalesInvoiceDTO>>({});
  const [loadingDetail,  setLoadingDetail]  = useState<Set<number>>(new Set());

  // ── Restaurant order expand / detail state — mirrors the invoice one above
  const [expandedOrderRows, setExpandedOrderRows] = useState<Set<number>>(new Set());
  const [orderDetails,      setOrderDetails]      = useState<Record<number, RestaurantOrderDTO>>({});
  const [loadingOrderDetail, setLoadingOrderDetail] = useState<Set<number>>(new Set());
  const [orderRowError,     setOrderRowError]     = useState<Record<number, string>>({});

  // ── PDF / export state
  const [viewingPdf,     setViewingPdf]     = useState<Set<number>>(new Set());
  const [downloadingPdf, setDownloadingPdf] = useState<Set<number>>(new Set());
  const [exportingCsv,   setExportingCsv]   = useState(false);
  const [exportingPdf,   setExportingPdf]   = useState(false);
  const [rowError,       setRowError]       = useState<Record<number, string>>({});

  // ── Restaurant order PDF state — mirrors the invoice one above
  const [viewingOrderPdf,     setViewingOrderPdf]     = useState<Set<number>>(new Set());
  const [downloadingOrderPdf, setDownloadingOrderPdf] = useState<Set<number>>(new Set());

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSearch = async () => {
    setSearching(true);
    setHasSearched(true);
    setExpandedRows(new Set());
    setInvoiceDetails({});
    setRowError({});
    setExpandedOrderRows(new Set());
    setOrderDetails({});
    setOrderRowError({});
    try {
      const res    = await salesInvoiceService.searchByDateRange(filters);
      const result: SalesSearchResultDTO = (res.data as any)?.data ?? res.data ?? { invoices: [], restaurantOrders: [] };
      const invoiceData = result.invoices ?? [];
      const orderData   = result.restaurantOrders ?? [];
      setInvoices(invoiceData);
      setRestaurantOrders(orderData);
      setMetrics(computeMetrics(invoiceData, orderData));
    } catch {
      setInvoices([]);
      setRestaurantOrders([]);
      setMetrics(null);
    } finally {
      setSearching(false);
    }
  };

  const handleFilterChange = (field: keyof SearchFilters, value: string) =>
    setFilters(prev => ({ ...prev, [field]: value }));

  const toggleDetail = async (invoice: SalesInvoiceListDTO) => {
    const id = invoice.id;
    if (expandedRows.has(id)) {
      setExpandedRows(prev => { const s = new Set(prev); s.delete(id); return s; });
      return;
    }
    setExpandedRows(prev => new Set(prev).add(id));
    if (invoiceDetails[id]) return;
    setLoadingDetail(prev => new Set(prev).add(id));
    try {
      const res    = await salesInvoiceService.getSalesInvoiceById(id);
      const detail = (res.data as any)?.data ?? res.data;
      setInvoiceDetails(prev => ({ ...prev, [id]: detail }));
    } catch {
      setRowError(prev => ({ ...prev, [id]: t('common.loadError', 'Failed to load detail') }));
    } finally {
      setLoadingDetail(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const toggleOrderDetail = async (order: RestaurantOrderSummaryDTO) => {
    const id = order.id;
    if (expandedOrderRows.has(id)) {
      setExpandedOrderRows(prev => { const s = new Set(prev); s.delete(id); return s; });
      return;
    }
    setExpandedOrderRows(prev => new Set(prev).add(id));
    if (orderDetails[id]) return;
    setLoadingOrderDetail(prev => new Set(prev).add(id));
    try {
      const res    = await restaurantOrderService.getById(id);
      const detail = (res.data as any)?.data ?? res.data;
      setOrderDetails(prev => ({ ...prev, [id]: detail }));
    } catch {
      setOrderRowError(prev => ({ ...prev, [id]: t('common.loadError', 'Failed to load detail') }));
    } finally {
      setLoadingOrderDetail(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleViewPdf = async (invoice: SalesInvoiceListDTO) => {
    const id = invoice.id;
    if (viewingPdf.has(id)) return;
    setViewingPdf(prev => new Set(prev).add(id));
    try {
      await salesInvoiceService.viewInvoicePdf(id, invoice.invoiceNumber);
    } catch {
      setRowError(prev => ({ ...prev, [id]: t('common.loadError', 'Failed to open PDF') }));
    } finally {
      setViewingPdf(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleDownloadPdf = async (invoice: SalesInvoiceListDTO) => {
    const id = invoice.id;
    if (downloadingPdf.has(id)) return;
    setDownloadingPdf(prev => new Set(prev).add(id));
    try {
      await salesInvoiceService.downloadInvoicePdf(id, invoice.invoiceNumber);
    } catch {
      setRowError(prev => ({ ...prev, [id]: t('common.loadError', 'Failed to download PDF') }));
    } finally {
      setDownloadingPdf(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleViewOrderPdf = async (order: RestaurantOrderSummaryDTO) => {
    const id = order.id;
    if (viewingOrderPdf.has(id)) return;
    setViewingOrderPdf(prev => new Set(prev).add(id));
    try {
      await restaurantOrderService.viewOrderPdf(id, order.orderNumber);
    } catch {
      setOrderRowError(prev => ({ ...prev, [id]: t('common.loadError', 'Failed to open PDF') }));
    } finally {
      setViewingOrderPdf(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleDownloadOrderPdf = async (order: RestaurantOrderSummaryDTO) => {
    const id = order.id;
    if (downloadingOrderPdf.has(id)) return;
    setDownloadingOrderPdf(prev => new Set(prev).add(id));
    try {
      await restaurantOrderService.downloadOrderPdf(id, order.orderNumber);
    } catch {
      setOrderRowError(prev => ({ ...prev, [id]: t('common.loadError', 'Failed to download PDF') }));
    } finally {
      setDownloadingOrderPdf(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleExportCsv = async () => {
    if (exportingCsv) return;
    setExportingCsv(true);
    try {
      const stamp = `${filters.startDate}_${filters.endDate}`;
      await salesInvoiceService.exportCsv(filters, `invoices_${stamp}.csv`);
    } finally {
      setExportingCsv(false);
    }
  };

  const handleExportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      const stamp = `${filters.startDate}_${filters.endDate}`;
      await salesInvoiceService.exportPdf(filters, `invoice-report_${stamp}.pdf`);
    } finally {
      setExportingPdf(false);
    }
  };

  const paymentRate = metrics && metrics.totalSales > 0
    ? ((metrics.paidAmount / metrics.totalSales) * 100).toFixed(1) + '%'
    : '—';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="ssr-page">

        {/* ── Hero ──────────────────────────────────────────────────── */}
        <div className="ssr-hero">
          <div>
            <h1 className="ssr-hero-title">
              <BarChart2 size={22} strokeWidth={1.8} />
              {t('salesReports.summaryTitle', 'Sales Summary')}
            </h1>
            <p className="ssr-hero-sub">
              {t('salesReports.summarySubtitle', 'Search invoices and restaurant orders by date and time range')}
            </p>
          </div>

          <div className="ssr-hero-actions">
            {hasSearched && invoices.length > 0 && (
              <div className="ssr-export-group">
                <button
                  className="ssr-export-btn ssr-export-csv"
                  onClick={handleExportCsv}
                  disabled={exportingCsv}
                >
                  <FileText size={13} strokeWidth={2} />
                  {exportingCsv ? t('common.exporting', 'Exporting…') : 'Export CSV'}
                </button>
                <button
                  className="ssr-export-btn ssr-export-pdf"
                  onClick={handleExportPdf}
                  disabled={exportingPdf}
                >
                  <Download size={13} strokeWidth={2} />
                  {exportingPdf ? t('common.exporting', 'Exporting…') : 'Export PDF'}
                </button>
              </div>
            )}
            <button className="ssr-back-btn" onClick={() => navigate('/reports/sales')}>
              <ArrowLeft size={14} strokeWidth={2} />
              {t('common.back', 'Back')}
            </button>
          </div>
        </div>

        {/* ── Filter card ───────────────────────────────────────────── */}
        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title">
              <Search size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {t('salesReports.dateRangeFilter', 'Date Range Filter')}
            </span>
          </div>
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="ssr-filter-bar">
              <div className="ssr-filter-group">
                <span className="ssr-filter-label">{t('salesReports.fromDateTime', 'From date / time')}</span>
                <div className="ssr-filter-row">
                  <input
                    type="date"
                    className="ims-form-control"
                    value={filters.startDate}
                    onChange={e => handleFilterChange('startDate', e.target.value)}
                  />
                  <input
                    type="time"
                    className="ims-form-control ssr-time-input"
                    value={filters.startTime}
                    onChange={e => handleFilterChange('startTime', e.target.value)}
                  />
                </div>
              </div>

              <div className="ssr-filter-group">
                <span className="ssr-filter-label">{t('salesReports.toDateTime', 'To date / time')}</span>
                <div className="ssr-filter-row">
                  <input
                    type="date"
                    className="ims-form-control"
                    value={filters.endDate}
                    onChange={e => handleFilterChange('endDate', e.target.value)}
                  />
                  <input
                    type="time"
                    className="ims-form-control ssr-time-input"
                    value={filters.endTime}
                    onChange={e => handleFilterChange('endTime', e.target.value)}
                  />
                </div>
              </div>

              <button
                className="ims-btn ims-btn-primary"
                onClick={handleSearch}
                disabled={searching}
                style={{ alignSelf: 'flex-end' }}
              >
                {searching ? (
                  <><span className="ssr-spinner" /> {t('common.searching', 'Searching…')}</>
                ) : (
                  <><Search size={14} strokeWidth={2.5} /> {t('common.search', 'Search')}</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── Initial prompt ────────────────────────────────────────── */}
        {!hasSearched && (
          <div className="ssr-prompt">
            <Search size={48} strokeWidth={1} />
            <p>
              {t('salesReports.searchPrompt', 'Set a date range and click')}{' '}
              <strong>{t('common.search', 'Search')}</strong>{' '}
              {t('salesReports.searchPromptEnd', 'to load invoices.')}
            </p>
          </div>
        )}

        {/* ── Results ───────────────────────────────────────────────── */}
        {hasSearched && !searching && (
          <>
            {/* KPI stat cards */}
            {metrics && (invoices.length > 0 || restaurantOrders.length > 0) && (
              <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-total"><Receipt size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value">{metrics.totalInvoices}</span>
                    <span className="ims-stat-label">{t('salesReports.totalInvoices', 'Total Invoices')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-rate"><ShoppingBag size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value">{metrics.totalRestaurantOrders}</span>
                    <span className="ims-stat-label">{t('salesReports.totalRestaurantOrders', 'Restaurant Orders')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-sales"><DollarSign size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.1rem' }}>{fmt(metrics.totalSales)}</span>
                    <span className="ims-stat-label">{t('salesReports.totalSales', 'Total Sales')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-paid"><CheckCircle2 size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.1rem' }}>{fmt(metrics.paidAmount)}</span>
                    <span className="ims-stat-label">{t('salesReports.amountPaid', 'Amount Paid')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-unpaid"><AlertTriangle size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.1rem' }}>{fmt(metrics.unpaidAmount)}</span>
                    <span className="ims-stat-label">{t('salesReports.balanceDue', 'Balance Due')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-avg"><TrendingUp size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.1rem' }}>{fmt(metrics.averageTransactionValue)}</span>
                    <span className="ims-stat-label">{t('salesReports.avgTransaction', 'Avg. Transaction Value')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-rate"><Percent size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value">{paymentRate}</span>
                    <span className="ims-stat-label">{t('salesReports.paymentRate', 'Payment Rate')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Invoice list */}
            {invoices.length === 0 ? (
              <div className="ims-card">
                <div className="ssr-empty">
                  {t('salesReports.noInvoices', 'No invoices found for the selected period.')}
                </div>
              </div>
            ) : (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <Receipt size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    {t('salesReports.invoices', 'Invoices')}
                    <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--ims-text-muted)', marginInlineStart: 6 }}>
                      ({invoices.length} {invoices.length !== 1
                        ? t('salesReports.records', 'records')
                        : t('salesReports.record', 'record')})
                    </span>
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="ssr-table">
                    <thead>
                      <tr>
                        <th>{t('salesReports.colInvoice', 'Invoice #')}</th>
                        <th>{t('salesReports.colCustomer', 'Customer')}</th>
                        <th>{t('salesReports.colDate', 'Date')}</th>
                        <th>{t('salesReports.colDueDate', 'Due Date')}</th>
                        <th>{t('salesReports.colStatus', 'Status')}</th>
                        <th>{t('salesReports.colPayment', 'Payment')}</th>
                        <th className="num">{t('salesReports.colTotal', 'Total')}</th>
                        <th className="num">{t('salesReports.colPaid', 'Paid')}</th>
                        <th className="num">{t('salesReports.colBalance', 'Balance')}</th>
                        <th>{t('salesReports.colActions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(inv => (
                        <React.Fragment key={inv.id}>
                          <tr>
                            <td>
                              <span className="ssr-inv-num">{inv.invoiceNumber}</span>
                            </td>
                            <td>{inv.customerName}</td>
                            <td>{new Date(inv.invoiceDate).toLocaleDateString()}</td>
                            <td>
                              <span className={inv.isOverdue ? 'ssr-overdue-date' : ''}>
                                {new Date(inv.dueDate).toLocaleDateString()}
                              </span>
                              {inv.isOverdue && (
                                <span className="ssr-overdue-tag">
                                  {t('salesReports.overdue', 'Overdue')}
                                </span>
                              )}
                            </td>
                            <td>
                              <span className={statusBadgeClass(inv.status)}>{inv.status}</span>
                            </td>
                            <td>
                              <span className={statusBadgeClass(inv.paymentStatus)}>{inv.paymentStatus}</span>
                            </td>
                            <td className="num">{fmt(inv.totalAmount)}</td>
                            <td className="num">{fmt(inv.amountPaid)}</td>
                            <td className={`num ${inv.balanceDue > 0 ? 'ssr-balance-due' : 'ssr-balance-zero'}`}>
                              {fmt(inv.balanceDue)}
                            </td>
                            <td>
                              <div className="ssr-row-actions">
                                <button
                                  className="ssr-btn-row ssr-btn-detail"
                                  onClick={() => toggleDetail(inv)}
                                  disabled={loadingDetail.has(inv.id)}
                                  title={t('salesReports.toggleDetail', 'Show / hide line items')}
                                >
                                  {loadingDetail.has(inv.id)
                                    ? '…'
                                    : expandedRows.has(inv.id)
                                      ? <><ChevronUp size={11} /> {t('salesReports.hide', 'Hide')}</>
                                      : <><ChevronDown size={11} /> {t('salesReports.detail', 'Detail')}</>
                                  }
                                </button>
                                <button
                                  className="ssr-btn-row ssr-btn-view"
                                  onClick={() => handleViewPdf(inv)}
                                  disabled={viewingPdf.has(inv.id)}
                                  title={t('salesReports.viewPdf', 'Open PDF in new tab')}
                                >
                                  {viewingPdf.has(inv.id)
                                    ? '…'
                                    : <><Eye size={11} /> {t('salesReports.view', 'View')}</>
                                  }
                                </button>
                                <button
                                  className="ssr-btn-row ssr-btn-download"
                                  onClick={() => handleDownloadPdf(inv)}
                                  disabled={downloadingPdf.has(inv.id)}
                                  title={t('salesReports.downloadPdf', 'Download PDF')}
                                >
                                  {downloadingPdf.has(inv.id)
                                    ? '…'
                                    : <><Download size={11} /> PDF</>
                                  }
                                </button>
                              </div>
                              {rowError[inv.id] && (
                                <div className="ssr-row-err">{rowError[inv.id]}</div>
                              )}
                            </td>
                          </tr>

                          {/* Expandable line items */}
                          {expandedRows.has(inv.id) && (
                            <tr className="ssr-detail-row">
                              <td colSpan={10}>
                                <div className="ssr-detail-inner">
                                  {loadingDetail.has(inv.id) ? (
                                    <p style={{ color: 'var(--ims-text-muted)', fontSize: 12 }}>
                                      {t('common.loading', 'Loading…')}
                                    </p>
                                  ) : invoiceDetails[inv.id]?.lines?.length ? (
                                    <>
                                      <h4 className="ssr-detail-title">
                                        {t('salesReports.lineItems', 'Line Items')} — {inv.invoiceNumber}
                                      </h4>
                                      <table className="ssr-lines-table">
                                        <thead>
                                          <tr>
                                            <th>{t('salesReports.product', 'Product')}</th>
                                            <th>{t('salesReports.sku', 'SKU')}</th>
                                            <th className="num">{t('salesReports.qty', 'Qty')}</th>
                                            <th className="num">{t('salesReports.unitPrice', 'Unit Price')}</th>
                                            <th className="num">{t('salesReports.discount', 'Discount')}</th>
                                            <th className="num">{t('salesReports.tax', 'Tax')}</th>
                                            <th className="num">{t('salesReports.lineTotal', 'Line Total')}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {invoiceDetails[inv.id].lines.map(line => (
                                            <tr key={line.id}>
                                              <td style={{ fontWeight: 500 }}>{line.productName}</td>
                                              <td style={{ color: 'var(--ims-text-muted)' }}>{line.productSKU}</td>
                                              <td className="num">{line.quantity}</td>
                                              <td className="num">{fmt(line.unitPrice)}</td>
                                              <td className="num">
                                                {line.discountAmount > 0 ? fmt(line.discountAmount) : '—'}
                                              </td>
                                              <td className="num">
                                                {line.taxRate > 0
                                                  ? `${line.taxRate}% (${fmt(line.taxAmount)})`
                                                  : '—'}
                                              </td>
                                              <td className="num" style={{ fontWeight: 600 }}>
                                                {fmt(line.lineTotal)}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      <div className="ssr-detail-totals">
                                        <span>
                                          {t('salesReports.subtotal', 'Subtotal')}: <strong>{fmt(inv.subtotal)}</strong>
                                        </span>
                                        {inv.taxAmount > 0 && (
                                          <span>
                                            {t('salesReports.tax', 'Tax')}: <strong>{fmt(inv.taxAmount)}</strong>
                                          </span>
                                        )}
                                        {inv.discountAmount > 0 && (
                                          <span>
                                            {t('salesReports.discount', 'Discount')}: <strong>-{fmt(inv.discountAmount)}</strong>
                                          </span>
                                        )}
                                        <span className="ssr-detail-total-grand">
                                          {t('salesReports.total', 'Total')}: <strong>{fmt(inv.totalAmount)}</strong>
                                        </span>
                                      </div>
                                    </>
                                  ) : (
                                    <p style={{ color: 'var(--ims-text-muted)', fontSize: 12 }}>
                                      {t('salesReports.noLineItems', 'No line items found.')}
                                    </p>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Restaurant orders — separate table since order fields (no due date,
                different status set) don't map cleanly onto the invoice columns */}
            {restaurantOrders.length > 0 && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <ShoppingBag size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    {t('salesReports.restaurantOrders', 'Restaurant Orders')}
                    <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--ims-text-muted)', marginInlineStart: 6 }}>
                      ({restaurantOrders.length} {restaurantOrders.length !== 1
                        ? t('salesReports.records', 'records')
                        : t('salesReports.record', 'record')})
                    </span>
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="ssr-table">
                    <thead>
                      <tr>
                        <th>{t('salesReports.colOrder', 'Order #')}</th>
                        <th>{t('salesReports.colCustomer', 'Customer')}</th>
                        <th>{t('salesReports.colDate', 'Date')}</th>
                        <th>{t('salesReports.colStatus', 'Status')}</th>
                        <th>{t('salesReports.colPayment', 'Payment')}</th>
                        <th className="num">{t('salesReports.colTotal', 'Total')}</th>
                        <th className="num">{t('salesReports.colPaid', 'Paid')}</th>
                        <th className="num">{t('salesReports.colBalance', 'Balance')}</th>
                        <th>{t('salesReports.colActions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {restaurantOrders.map(o => (
                        <React.Fragment key={o.id}>
                          <tr>
                            <td><span className="ssr-inv-num">{o.orderNumber}</span></td>
                            <td>{o.customerName || '—'}</td>
                            <td>{new Date(o.orderDate).toLocaleDateString()}</td>
                            <td>
                              <span className={restaurantStatusBadgeClass(o.status)}>{o.status}</span>
                            </td>
                            <td>
                              <span className={statusBadgeClass(orderPaymentStatus(o))}>{orderPaymentStatus(o)}</span>
                            </td>
                            <td className="num">{fmt(o.totalAmount)}</td>
                            <td className="num">{fmt(o.amountPaid)}</td>
                            <td className={`num ${o.balanceDue > 0 ? 'ssr-balance-due' : 'ssr-balance-zero'}`}>
                              {fmt(o.balanceDue)}
                            </td>
                            <td>
                              <div className="ssr-row-actions">
                                <button
                                  className="ssr-btn-row ssr-btn-detail"
                                  onClick={() => toggleOrderDetail(o)}
                                  disabled={loadingOrderDetail.has(o.id)}
                                  title={t('salesReports.toggleDetail', 'Show / hide line items')}
                                >
                                  {loadingOrderDetail.has(o.id)
                                    ? '…'
                                    : expandedOrderRows.has(o.id)
                                      ? <><ChevronUp size={11} /> {t('salesReports.hide', 'Hide')}</>
                                      : <><ChevronDown size={11} /> {t('salesReports.detail', 'Detail')}</>
                                  }
                                </button>
                                <button
                                  className="ssr-btn-row ssr-btn-view"
                                  onClick={() => handleViewOrderPdf(o)}
                                  disabled={viewingOrderPdf.has(o.id)}
                                  title={t('salesReports.viewPdf', 'Open PDF in new tab')}
                                >
                                  {viewingOrderPdf.has(o.id)
                                    ? '…'
                                    : <><Eye size={11} /> {t('salesReports.view', 'View')}</>
                                  }
                                </button>
                                <button
                                  className="ssr-btn-row ssr-btn-download"
                                  onClick={() => handleDownloadOrderPdf(o)}
                                  disabled={downloadingOrderPdf.has(o.id)}
                                  title={t('salesReports.downloadPdf', 'Download PDF')}
                                >
                                  {downloadingOrderPdf.has(o.id)
                                    ? '…'
                                    : <><Download size={11} /> PDF</>
                                  }
                                </button>
                              </div>
                              {orderRowError[o.id] && (
                                <div className="ssr-row-err">{orderRowError[o.id]}</div>
                              )}
                            </td>
                          </tr>

                          {/* Expandable line items */}
                          {expandedOrderRows.has(o.id) && (
                            <tr className="ssr-detail-row">
                              <td colSpan={9}>
                                <div className="ssr-detail-inner">
                                  {loadingOrderDetail.has(o.id) ? (
                                    <p style={{ color: 'var(--ims-text-muted)', fontSize: 12 }}>
                                      {t('common.loading', 'Loading…')}
                                    </p>
                                  ) : orderDetails[o.id]?.lines?.length ? (
                                    <>
                                      <h4 className="ssr-detail-title">
                                        {t('salesReports.lineItems', 'Line Items')} — {o.orderNumber}
                                      </h4>
                                      <table className="ssr-lines-table">
                                        <thead>
                                          <tr>
                                            <th>{t('salesReports.product', 'Item')}</th>
                                            <th className="num">{t('salesReports.qty', 'Qty')}</th>
                                            <th className="num">{t('salesReports.unitPrice', 'Unit Price')}</th>
                                            <th className="num">{t('salesReports.discount', 'Discount')}</th>
                                            <th className="num">{t('salesReports.lineTotal', 'Line Total')}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {orderDetails[o.id].lines.map(line => (
                                            <tr key={line.id}>
                                              <td style={{ fontWeight: 500 }}>{line.itemName}</td>
                                              <td className="num">{line.quantity}</td>
                                              <td className="num">{fmt(line.unitPrice)}</td>
                                              <td className="num">
                                                {line.discountAmount > 0 ? fmt(line.discountAmount) : '—'}
                                              </td>
                                              <td className="num" style={{ fontWeight: 600 }}>
                                                {fmt(line.lineTotal)}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      <div className="ssr-detail-totals">
                                        <span>
                                          {t('salesReports.subtotal', 'Subtotal')}: <strong>{fmt(orderDetails[o.id].subTotal)}</strong>
                                        </span>
                                        {orderDetails[o.id].taxAmount > 0 && (
                                          <span>
                                            {t('salesReports.tax', 'Tax')}: <strong>{fmt(orderDetails[o.id].taxAmount)}</strong>
                                          </span>
                                        )}
                                        {orderDetails[o.id].discountAmount > 0 && (
                                          <span>
                                            {t('salesReports.discount', 'Discount')}: <strong>-{fmt(orderDetails[o.id].discountAmount)}</strong>
                                          </span>
                                        )}
                                        <span className="ssr-detail-total-grand">
                                          {t('salesReports.total', 'Total')}: <strong>{fmt(o.totalAmount)}</strong>
                                        </span>
                                      </div>
                                    </>
                                  ) : (
                                    <p style={{ color: 'var(--ims-text-muted)', fontSize: 12 }}>
                                      {t('salesReports.noLineItems', 'No line items found.')}
                                    </p>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Invoice breakdown */}
            {metrics && invoices.length > 0 && (
              <>
                <span className="ssr-section-title">
                  {t('salesReports.invoiceBreakdown', 'Invoice Breakdown')}
                </span>
                <div className="ssr-breakdown-grid">
                  <div className="ssr-breakdown-card ssr-breakdown-paid">
                    <div className="ssr-breakdown-label">{t('salesReports.paid', 'Paid')}</div>
                    <div className="ssr-breakdown-value">{metrics.paidInvoices}</div>
                    <div className="ssr-breakdown-pct">
                      {metrics.totalInvoices > 0
                        ? ((metrics.paidInvoices / metrics.totalInvoices) * 100).toFixed(1) + '%'
                        : '0.0%'}
                    </div>
                  </div>

                  <div className="ssr-breakdown-card ssr-breakdown-partial">
                    <div className="ssr-breakdown-label">{t('salesReports.partiallyPaid', 'Partially Paid')}</div>
                    <div className="ssr-breakdown-value">{metrics.partiallyPaidInvoices}</div>
                    <div className="ssr-breakdown-pct">
                      {metrics.totalInvoices > 0
                        ? ((metrics.partiallyPaidInvoices / metrics.totalInvoices) * 100).toFixed(1) + '%'
                        : '0.0%'}
                    </div>
                  </div>

                  <div className="ssr-breakdown-card ssr-breakdown-unpaid">
                    <div className="ssr-breakdown-label">{t('salesReports.unpaid', 'Unpaid')}</div>
                    <div className="ssr-breakdown-value">{metrics.unpaidInvoices}</div>
                    <div className="ssr-breakdown-pct">
                      {metrics.totalInvoices > 0
                        ? ((metrics.unpaidInvoices / metrics.totalInvoices) * 100).toFixed(1) + '%'
                        : '0.0%'}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

      </div>
    </Layout>
  );
};

export default SalesSummaryReportPage;
